import { useMemo } from 'react'

import { type QuoteResult } from 'components/quoting/types'
import { AllowanceStrategies, type AllowanceStrategy } from 'pages/employer_onboarding/quote/types'
import { calculateEmployeeMonthlyAllowanceForEmployeeOnly, flatAllowanceIsAffordable, planIsAffordable } from 'utils/quote_calculations'

const FAIR_ALLOWANCE_YOUNGEST_OLDEST_RATIO = 3

export function getEmployeesWithUnaffordablePlans (
  quoteResults: QuoteResult[],
  allowanceType: AllowanceStrategy,
  employerEmployeeContribution: number,
  calculatedCostsByEmployeeId?: Record<string, { employeeAllowanceAmount: number }>
): QuoteResult[] {
  if (allowanceType === AllowanceStrategies.Smart) {
    return quoteResults.filter((result) =>
      !planIsAffordable(result, {
        employerEmployeeContribution,
      })
    )
  }
  return quoteResults.filter((result) => {
    const calculatedCost = calculatedCostsByEmployeeId?.[result.id]?.employeeAllowanceAmount
    const employerCost = calculatedCost ?? result.employer_medical_contribution
    return !flatAllowanceIsAffordable(result, employerCost)
  })
}

export interface ComplianceCalculations {
  employeesWithUnaffordablePlans: QuoteResult[]
  youngestAndOldestEmployeesByRatingArea: Record<string, { youngest: QuoteResult, oldest: QuoteResult }>
  ratingAreasWithUnfairAllowances: [string, { youngest: QuoteResult, oldest: QuoteResult }][]
  numPassingChecks: number
  totalChecks: number
  allChecksPassed: boolean
}

export function useComplianceCalculations (
  quoteResults: QuoteResult[],
  allowanceType: AllowanceStrategy,
  employerEmployeeContribution: number,
  calculatedCostsByEmployeeId?: Record<string, { employeeAllowanceAmount: number }>
): ComplianceCalculations {
  const employeesWithUnaffordablePlans = useMemo(() => {
    return getEmployeesWithUnaffordablePlans(
      quoteResults,
      allowanceType,
      employerEmployeeContribution,
      calculatedCostsByEmployeeId
    )
  }, [quoteResults, allowanceType, employerEmployeeContribution, calculatedCostsByEmployeeId])

  const youngestAndOldestEmployeesByRatingArea = useMemo(() => {
    return quoteResults.reduce<Record<string, { youngest: QuoteResult, oldest: QuoteResult }>>((acc, result) => {
      acc[result.rating_area_code] ||= {
        youngest: result,
        oldest: result
      }

      const area = acc[result.rating_area_code]
      if (new Date(result.date_of_birth) > new Date(area.youngest.date_of_birth)) {
        area.youngest = result
      }

      if (new Date(result.date_of_birth) < new Date(area.oldest.date_of_birth)) {
        area.oldest = result
      }

      return acc
    }, {})
  }, [quoteResults])

  const ratingAreasWithUnfairAllowances = useMemo(() => {
    return Object.entries(youngestAndOldestEmployeesByRatingArea).filter(([_, employees]) => {
      return (calculateEmployeeMonthlyAllowanceForEmployeeOnly(employees.oldest, employerEmployeeContribution) / FAIR_ALLOWANCE_YOUNGEST_OLDEST_RATIO) >
        calculateEmployeeMonthlyAllowanceForEmployeeOnly(employees.youngest, employerEmployeeContribution)
    })
  }, [youngestAndOldestEmployeesByRatingArea, employerEmployeeContribution])

  const numPassingChecks = useMemo(() => {
    return Number(employeesWithUnaffordablePlans.length === 0) + Number(ratingAreasWithUnfairAllowances.length === 0)
  }, [employeesWithUnaffordablePlans, ratingAreasWithUnfairAllowances])

  const totalChecks = 2

  const allChecksPassed = numPassingChecks === totalChecks

  return {
    employeesWithUnaffordablePlans,
    youngestAndOldestEmployeesByRatingArea,
    ratingAreasWithUnfairAllowances,
    numPassingChecks,
    totalChecks,
    allChecksPassed,
  }
}
